import React, { useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { bulkInviteMembers, TeamMember } from '@/services/teamService';
import { Role } from '@/types/team';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useTeampageTranslations } from '@/hooks/useTeampageTranslations';
import { logError, logDebug } from '@/utils/errorHandler';

interface BulkInviteModalProps {
  show: boolean;
  onHide: () => void;
  roles: Role[];
  onSuccess?: () => void;
}

interface InviteEntry {
  email: string;
  isValid: boolean;
  message?: string;
}

type BulkInviteFormData = {
  invites: { email: string; role_id: number }[];
};

const BulkInviteModal: React.FC<BulkInviteModalProps> = ({ show, onHide, roles, onSuccess }) => {
  // Get translations
  const i18n = useTeampageTranslations();

  const { orgId } = useParams<{ orgId: string }>();
  const [emailsText, setEmailsText] = useState('');
  const [roleId, setRoleId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [validatedEmails, setValidatedEmails] = useState<InviteEntry[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const queryClient = useQueryClient();

  // Filter out OrgOwner role (cannot be assigned manually)
  const selectableRoles = roles.filter(role => role.name !== 'OrgOwner');

  const inviteMutation = useMutation<TeamMember[], Error, BulkInviteFormData>({
    mutationFn: (formData: BulkInviteFormData) => bulkInviteMembers(formData.invites, orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      toast.success(i18n.bulkInviteModal.invitationsSent);
      if (onSuccess) onSuccess();
      handleClose();
    },
    onError: (err: any) => {
      logError(err instanceof Error ? err : new Error(String(err)), {
        component: 'BulkInviteModal',
        action: 'inviteMutation',
        metadata: {
          emailsCount: validatedEmails.length,
          roleId,
          orgId,
          context: 'bulk_invitation_error',
        },
      });

      // Extract error message from the response if available
      let errorMessage = i18n.bulkInviteModal.invitationsFailed;

      if (err.response) {
        // Handle structured API errors
        if (err.response.data && err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data && typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.status === 404) {
          errorMessage = i18n.bulkInviteModal.endpointNotFound;
        } else if (err.response.status === 401) {
          errorMessage = i18n.bulkInviteModal.notAuthorized;
        } else if (err.response.status === 400) {
          errorMessage = i18n.bulkInviteModal.invalidInput;
        }

        // Handle partial failures with detailed response
        if (err.response.data && err.response.data.results) {
          const failures = err.response.data.results.filter((r: any) => !r.success);
          if (failures.length > 0) {
            errorMessage = i18n.bulkInviteModal.partialFailure(failures.length);
            // Show the failed emails in the validation results
            setValidatedEmails(prev =>
              prev.map(entry => {
                const failure = failures.find((f: any) => f.email === entry.email);
                if (failure) {
                  return {
                    ...entry,
                    isValid: false,
                    message: failure.message || i18n.bulkInviteModal.failedToInvite,
                  };
                }
                return entry;
              })
            );
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  const validateEmails = useCallback(() => {
    setIsValidating(true);
    setError(null);

    try {
      // Split by commas or newlines
      const emails = emailsText
        .split(/[\n,]/)
        .map(e => e.trim())
        .filter(e => e.length > 0);

      if (emails.length === 0) {
        setValidatedEmails([]);
        setIsValidating(false);
        return false;
      }

      // Validate each email
      const validated = emails.map(email => {
        const emailRegex =
          /^[a-zA-Z0-9.!#$%'*+/=?^_`{|}~]+@[a-zA-Z0-9](?:[a-zA-Z0-9]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9]*[a-zA-Z0-9])?)*$/;
        const isValid = emailRegex.test(email);

        return {
          email,
          isValid,
          message: isValid ? undefined : i18n.bulkInviteModal.invalidEmailFormat,
        };
      });

      setValidatedEmails(validated);
      setIsValidating(false);

      // Check if all emails are valid
      return validated.every(entry => entry.isValid);
    } catch {
      setError(i18n.bulkInviteModal.errorValidating);
      setIsValidating(false);
      return false;
    }
  }, [emailsText, i18n.bulkInviteModal.invalidEmailFormat, i18n.bulkInviteModal.errorValidating]);

  // Validate emails whenever emailsText changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (emailsText.trim()) {
        validateEmails();
      } else {
        setValidatedEmails([]);
      }
    }, 500); // Debounce validation by 500ms

    return () => clearTimeout(timer);
  }, [emailsText, validateEmails]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!emailsText.trim()) {
      setError(i18n.bulkInviteModal.emailsRequired);
      return;
    }

    if (!roleId) {
      setError(i18n.bulkInviteModal.selectRole);
      return;
    }

    // Check if all emails are valid
    const allValid = validatedEmails.length > 0 && validatedEmails.every(entry => entry.isValid);

    if (allValid) {
      // Prepare data for API call
      const invites = validatedEmails
        .filter(entry => entry.isValid)
        .map(entry => ({
          email: entry.email,
          role_id: parseInt(roleId, 10),
        }));

      logDebug('Sending bulk invites to API', {
        invites,
        orgId,
      });

      inviteMutation.mutate({ invites });
    }
  };

  const handleClose = () => {
    setEmailsText('');
    setRoleId('');
    setError(null);
    setValidatedEmails([]);
    inviteMutation.reset();
    onHide();
  };

  const handleSampleFormat = () => {
    setEmailsText(i18n.bulkInviteModal.sampleEmails);
  };

  if (!show) return null;

  const validCount = validatedEmails.filter(e => e.isValid).length;
  const invalidCount = validatedEmails.filter(e => !e.isValid).length;

  return (
    <Dialog open={show} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{i18n.bulkInviteModal.title}</DialogTitle>
          <DialogDescription>{i18n.bulkInviteModal.description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {error && (
            <Alert className="mb-4">
              <AlertDescription className="text-red-600">{error}</AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="emails">{i18n.bulkInviteModal.emailsLabel}</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleSampleFormat}
                  className="h-auto p-1 text-xs"
                >
                  <FileText className="mr-1 size-3" />
                  {i18n.bulkInviteModal.sampleFormatButton}
                </Button>
              </div>
              <Textarea
                id="emails"
                placeholder={i18n.bulkInviteModal.emailsPlaceholder}
                value={emailsText}
                onChange={e => setEmailsText(e.target.value)}
                disabled={inviteMutation.isPending}
                rows={6}
                className="resize-none"
              />
              <p className="text-sm text-muted-foreground">{i18n.bulkInviteModal.emailsHelp}</p>

              {/* Validation status */}
              {isValidating && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  {i18n.bulkInviteModal.validating}
                </div>
              )}

              {validatedEmails.length > 0 && !isValidating && (
                <div className="space-y-1 text-sm">
                  {validCount > 0 && (
                    <div className="text-green-600">
                      ✓ {i18n.bulkInviteModal.validEmails(validCount)}
                    </div>
                  )}
                  {invalidCount > 0 && (
                    <div className="text-red-600">
                      ✗ {i18n.bulkInviteModal.invalidEmails(invalidCount)}
                    </div>
                  )}
                </div>
              )}

              {/* Show invalid emails */}
              {validatedEmails.some(e => !e.isValid) && (
                <div className="space-y-1 text-sm text-red-600">
                  {validatedEmails
                    .filter(e => !e.isValid)
                    .slice(0, 3) // Show only first 3 invalid emails
                    .map((entry, index) => (
                      <div key={index} className="flex items-center">
                        <X className="mr-1 size-3" />
                        {entry.email}: {entry.message}
                      </div>
                    ))}
                  {invalidCount > 3 && (
                    <div className="text-xs text-muted-foreground">
                      +{invalidCount - 3} more invalid emails...
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="role">{i18n.bulkInviteModal.roleLabel}</Label>
              <Select value={roleId} onValueChange={setRoleId} disabled={inviteMutation.isPending}>
                <SelectTrigger>
                  <SelectValue placeholder={i18n.bulkInviteModal.rolePlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {selectableRoles.map(role => (
                    <SelectItem key={role.id} value={role.id.toString()}>
                      {i18n.roles[role.name as keyof typeof i18n.roles]?.name || role.name}
                      {role.description &&
                        ` - ${i18n.roles[role.name as keyof typeof i18n.roles]?.description || role.description}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={inviteMutation.isPending}>
                {i18n.bulkInviteModal.cancel}
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={inviteMutation.isPending || validCount === 0 || !roleId}
            >
              {inviteMutation.isPending
                ? i18n.bulkInviteModal.sending
                : i18n.bulkInviteModal.sendInvitations}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BulkInviteModal;
